<template><div id="annotations-editor" /></template>

<script setup lang="ts">
import { onMounted, ref, Ref, onUnmounted, watch } from 'vue';
import { ImageEditor } from '@cloudapp/pintura-config';
import { useRouter } from 'vue-router';
import { TrackEvents, useTrack } from '@/composables/useTrack';
import { useViewerItemStore } from '../../store/useViewerItemStore';

const props = defineProps<{
  src?: string;
}>();

const viewerItemStore = useViewerItemStore();
const { trackItemOwner } = useTrack();

let editor: any = null;
const isDirty: Ref<boolean> = ref(false);
const isUploading: Ref<boolean> = ref(false);
const trackTools: Ref<string[]> = ref([]);

const router = useRouter();

const bindEditor = () => {
  editor = new ImageEditor('#annotations-editor', {});

  editor.render(props.src).then((imageData: string) => {
    trackItemOwner(TrackEvents.ANNOTATE_SAVED, { tools: trackTools.value });
    trackTools.value = [];

    viewerItemStore.saveAnnotatedImage(imageData);

    router.push({ name: 'viewerMain' });
  });

  editor.on('tool_selected', (name: string) => {
    if (!trackTools.value || !Array.isArray(trackTools.value)) {
      trackTools.value = [];
    }

    if (!trackTools.value.includes(name)) {
      trackTools.value.push(name);
    }
  });

  editor.on('dirty', (dirtyState: boolean) => (isDirty.value = dirtyState));
  editor.on('close', () => {
    window.onbeforeunload = null;
    trackItemOwner(TrackEvents.ANNOTATE_CANCELED);
    router.push({ name: 'viewerMain' });
  });

  editor.on('revert', () => trackItemOwner(TrackEvents.ANNOTATE_REVERTED));
};

watch(isDirty, (val: boolean) => {
  if (val && !window.onbeforeunload) {
    window.onbeforeunload = () => 'You have unsaved changes';
    return;
  }

  window.onbeforeunload = null;
});

watch(isUploading, (val: boolean) => {
  if (val) {
    window.onbeforeunload = () => 'Changes are still uploading';
    return;
  }

  window.onbeforeunload = null;
});

onMounted(() => {
  if (editor) {
    editor.destroy();
    editor = null;
  }

  bindEditor();
});

onUnmounted(() => {
  isDirty.value = false;
  trackTools.value = [];
  isUploading.value = false;

  editor.destroy();
  editor = null;
});
</script>
